import dragIcon from "@assets/images/drag_indicator.svg";
import plusIcon from "@assets/images/plus-teal.svg";
import Trash from "@assets/images/trash-teal.svg";
import { Box, Grid } from "@material-ui/core";
import InputSuggestion from "@saleor/alternatedMaterial/components/InputSuggestionForPrioridy";
import DragHandle from "@saleor/components/Draggable";
import MyTypography from "@saleor/components/MyTypography";
import SingleTextField from "@saleor/components/SingleTextField";
import {
  useGradeGramLazyQuery,
  useMaterialOwnLazyQuery
} from "@saleor/graphql";
import { mapEdgesToItems } from "@saleor/utils/maps";
import moment from "moment";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import SVG from "react-inlinesvg";

import { useAddAltermatedMaterial } from "./AddAlternatedContext";
import { ALT_MATERIAL_TYPE } from "./AddItem";
import useStyles from "./styles";

interface MATERIAL_PRIORITY {
  materialOsId: string;
  id: number;
  priority?: number;
  displayText?: string;
  disabled?: boolean;
  dia?: string;
  isSubmitted?: boolean;
}

interface MaterialPrioridyComponentProps {
  onRemove: (id: number) => void;
  id: number;
  value: MATERIAL_PRIORITY;
  handleChange: (id: number, fieldName: string, value) => void;
  isShowDeleteButton: boolean;
  priority?: number;
  errorMsg?: string;
  isSubmitted?: boolean;
  saleOrganization?: string;
}

interface GradeGramPrioridyComponentProps {
  onRemove: (id: number) => void;
  id: number;
  value: MATERIAL_PRIORITY;
  handleChange: (id: number, fieldName: string, value) => void;
  isShowDeleteButton: boolean;
  priority?: number;
  errorMsg?: string;
  isSubmitted?: boolean;
  saleOrganization?: string;
}

const MaterialPriorityComponent: React.FC<MaterialPrioridyComponentProps> = ({
  onRemove,
  id,
  value,
  handleChange,
  isShowDeleteButton,
  errorMsg = "",
  priority,
  isSubmitted,
  saleOrganization = ""
}) => {
  const classes = useStyles();
  const [clearOnSaleChanges, setClearOnSaleChanges] = useState(false);
  const [
    getMaterialDataSuggestions,
    { data: materialDataSuggestions, loading: loadingMaterialDataSuggestion }
  ] = useMaterialOwnLazyQuery();

  const materialDataOptions = (
    mapEdgesToItems(materialDataSuggestions?.materialAlter) || []
  ).map(data => ({
    title: `${data.code}`,
    value: `${data.id}`
  }));

  const error = useMemo(() => {
    if (!value.materialOsId && isSubmitted) {
      return "โปรดระบุข้อมูล";
    }
    if (errorMsg) {
      return errorMsg;
    }
    return "";
  }, [!value.materialOsId, isSubmitted]);

  const handleClearInputOnTypeChanges = () => {
    setClearOnSaleChanges(true);
    setTimeout(() => {
      setClearOnSaleChanges(false);
    }, 500);
  };

  useEffect(() => {
    handleClearInputOnTypeChanges();
  }, [saleOrganization]);

  return (
    <>
      <Box className={classes.priorityWrapper} key={id} id={`${id}`}>
        <Grid container className={classes.gradeGram}>
          <Grid
            item
            xs={12}
            md={isShowDeleteButton ? 11 : 12}
            style={{
              maxWidth: isShowDeleteButton ? "85%" : "99%",
              flexBasis: isShowDeleteButton ? "85%" : "99%"
            }}
          >
            <Box>
              <div className={classes.priorityHeader}>
                <Box className={classes.flex}>
                  <MyTypography variant={"subtitle3"}>
                    Alternated Material
                  </MyTypography>
                  <span className={classes.required}>*</span>
                </Box>
              </div>
              <div style={{ width: "100%" }}>
                <InputSuggestion
                  value={value.displayText}
                  defaultInputValue={value.displayText}
                  searchField="materialCode"
                  dataOptions={materialDataOptions}
                  totalCount={materialDataOptions.length}
                  loadingDataSuggestion={loadingMaterialDataSuggestion}
                  getDataSuggestions={getMaterialDataSuggestions}
                  contained
                  isCheckingRole
                  name={"materialOsId"}
                  id={`materialOsId-${id}`}
                  testId={`materialOsId-${id}`}
                  placeholder={"ค้นหา Alternated Material"}
                  objectSuggestion
                  key={priority}
                  error={!!error}
                  handleSelectedSearch={value => {
                    handleChange(id, "materialOsId", value.value);
                    handleChange(id, "displayText", value?.label);
                  }}
                  saleOrganization={saleOrganization}
                  isClean={clearOnSaleChanges}
                  resetInputValueOnBlur
                />
              </div>

              <div className={classes.textError}>{error}</div>
            </Box>
          </Grid>
        </Grid>
        {isShowDeleteButton && (
          <Grid item xs={12} md={2} className={classes.diadrop}>
            <Box
              onClick={() => {
                onRemove(id);
              }}
              className={classes.removeButton}
            >
              <img src={Trash} alt="trash-icon"></img>
            </Box>
            <span className={`column-drag-handle ${classes.dragIcon}`}>
              <SVG
                color="#999999"
                height={"22px"}
                style={{ margin: "0 3px" }}
                src={dragIcon}
              />
            </span>
          </Grid>
        )}
      </Box>
    </>
  );
};

const GradeGramPriorityComponent: React.FC<GradeGramPrioridyComponentProps> = ({
  onRemove,
  id,
  value,
  handleChange,
  isShowDeleteButton,
  errorMsg = "",
  priority,
  isSubmitted,
  saleOrganization = ""
}) => {
  const classes = useStyles();

  const [forcus, setForcus] = useState(false);

  useEffect(() => {
    setForcus(false);
  }, [saleOrganization]);

  const [
    getGradeGramDataSuggestions,
    { data: gradeGramDataSuggestions, loading: loadingGradeGramDataSuggestion }
  ] = useGradeGramLazyQuery();

  const gradeGramDataOptions = (
    mapEdgesToItems(gradeGramDataSuggestions?.materialAlter) || []
  ).map(data => ({
    title: data.code,
    value: data.id,
    dia: data.dia
  }));

  const error = useMemo(() => {
    if (!value.materialOsId && isSubmitted) {
      return "โปรดระบุข้อมูล";
    }
    if (errorMsg) {
      return errorMsg;
    }
    return "";
  }, [!value.materialOsId, isSubmitted]);

  return (
    <>
      <Box className={classes.priorityWrapper}>
        <Grid container className={classes.gradeGram}>
          <Grid
            item
            xs={12}
            md={isShowDeleteButton ? 5 : 6}
            style={{
              maxWidth: isShowDeleteButton ? "40%" : "47%",
              flexBasis: isShowDeleteButton ? "40%" : "47%"
            }}
          >
            <Box>
              <div className={classes.priorityHeader}>
                <Box className={classes.flex}>
                  <MyTypography variant={"subtitle3"}>
                    Alternated Grade/Gram
                  </MyTypography>
                  <span className={classes.required}>*</span>
                </Box>
              </div>
              {forcus ? (
                <div className={classes.singletext} style={{ width: "100%" }}>
                  <InputSuggestion
                    searchField="gradeGram"
                    dataOptions={gradeGramDataOptions}
                    totalCount={gradeGramDataOptions.length}
                    loadingDataSuggestion={loadingGradeGramDataSuggestion}
                    objectSuggestion
                    getDataSuggestions={getGradeGramDataSuggestions}
                    isCheckingRole
                    contained
                    defaultInputValue={value.displayText}
                    name={"materialOsId"}
                    id={`materialOsId-${id}`}
                    testId={`materialOsId-${id}`}
                    placeholder={"ค้นหา Alternated Grade/Gram"}
                    error={!!error}
                    key={priority}
                    handleSelectedSearch={selected => {
                      handleChange(id, "materialOsId", selected?.value);
                      handleChange(id, "displayText", selected?.title);
                    }}
                    saleOrganization={saleOrganization}
                    resetInputValueOnBlur
                  />
                </div>
              ) : (
                <div className={classes.singletext} style={{ width: "100%" }}>
                  <SingleTextField
                    value={value.displayText}
                    onMouseEnter={() => setForcus(true)}
                    onChange={null}
                    placeholder={"ค้นหา Alternated Grade/Gram"}
                    error={!!error}
                    readOnly
                    id={`materialOsId-${id}`}
                    InputProps={{
                      inputProps: { "data-testid": `gradeGram-${id}-input` }
                    }}
                  />
                </div>
              )}

              <div className={classes.textError}>{error}</div>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            md={isShowDeleteButton ? 5 : 6}
            style={{
              maxWidth: isShowDeleteButton ? "40%" : "47%",
              flexBasis: isShowDeleteButton ? "40%" : "47%"
            }}
          >
            <Box>
              <div className={classes.priorityHeader}>
                <Box className={classes.flex}>
                  <MyTypography variant={"subtitle3"}>Dia</MyTypography>
                  <span className={classes.required}>*</span>
                </Box>
              </div>
              <div className={classes.singletext} style={{ width: "100%" }}>
                <SingleTextField
                  error={!value.dia && isSubmitted}
                  onChange={e => {
                    handleChange(id, "dia", e.target.value);
                  }}
                  onKeyPress={e => {
                    const charCode = e.which ? e.which : e.keyCode;
                    const inputValue = e.target.value;
                    if (
                      (!/[0-9]/.test(e.key) &&
                        charCode !== 8 &&
                        charCode !== 46) ||
                      (inputValue.length >= 3 &&
                        charCode !== 8 &&
                        charCode !== 46)
                    ) {
                      e.preventDefault();
                    }
                  }}
                  placeholder="ระบุ Dia"
                  value={value.dia}
                  id={`dia-${id}`}
                  InputProps={{
                    inputProps: { "data-testid": `dia-${id}-input` }
                  }}
                />
              </div>
              {!value.dia && isSubmitted && (
                <div className={classes.textError}>โปรดระบุข้อมูล</div>
              )}
            </Box>
          </Grid>
          {isShowDeleteButton && (
            <Grid item xs={12} md={2} className={classes.diadrop}>
              <Box
                onClick={() => {
                  onRemove(id);
                }}
                className={classes.removeButton}
                data-testid={`remove-priority-${id}`}
              >
                <img src={Trash} alt="trash-icon"></img>
              </Box>
              <span className={`column-drag-handle ${classes.dragIcon}`}>
                <SVG
                  color="#999999"
                  height={"22px"}
                  style={{ margin: "0 3px" }}
                  src={dragIcon}
                />
              </span>
            </Grid>
          )}
        </Grid>
      </Box>
    </>
  );
};

const MaterialPriorityWrapperComponent = ({ type, saleOrganization }) => {
  const classes = useStyles();
  const [priorities, setPriorities] = useState<MATERIAL_PRIORITY[]>([
    {
      materialOsId: "",
      priority: 0,
      id: moment().valueOf()
    }
  ]);
  useEffect(() => {
    if (type !== "GradeGramPriority") {
      const tempPriority = [...priorities];
      tempPriority.forEach(element => {
        element.dia = "";
      });
      setPriorities(tempPriority);
    }
  }, [type]);

  useEffect(() => {
    const tempPriority = [...priorities];
    tempPriority.forEach(element => {
      element.dia = "";
      element.materialOsId = "";
      element.displayText = "";
    });
    formik.setFieldValue("priority", tempPriority);
    setPriorities(tempPriority);
  }, [saleOrganization]);

  const { formik, isSubmitted } = useAddAltermatedMaterial();
  const handleAddPriority = priority => {
    const newPriorities = [...priorities];

    const id = moment().valueOf();
    const newItem: MATERIAL_PRIORITY = {
      materialOsId: "",
      id,
      priority,
      displayText: "",
      disabled: false
    };

    newPriorities.push(newItem);
    setPriorities(newPriorities);
    formik.setFieldValue("priority", newPriorities);
  };

  const handleChangePriority = useCallback(
    (id: number, fieldName: string, value) => {
      if (id && fieldName) {
        const newPriorities = priorities.map(p => {
          if (p?.id === id) {
            p[fieldName] = value;
          }
          return p;
        });
        formik.setFieldValue("priority", newPriorities);
        setPriorities(newPriorities);
      }
    },
    [priorities]
  );
  const handleRearrangePriorty = (removedIndex, addedIndex) => {
    const tempPriority = [...priorities];

    const targetPriority = priorities[removedIndex];
    if (addedIndex <= removedIndex) {
      for (let i = removedIndex; i > addedIndex; i--) {
        tempPriority[i] = tempPriority[i - 1];
      }
    } else {
      for (let i = removedIndex; i < addedIndex; i++) {
        tempPriority[i] = tempPriority[i + 1];
      }
    }

    tempPriority[addedIndex] = targetPriority;
    for (let i = 0; i < tempPriority.length; i++) {
      tempPriority[i].priority = i;
    }

    formik.setFieldValue("priority", tempPriority);
    setPriorities(tempPriority);
  };
  const handleRemovePriority = useCallback(
    (id: number) => {
      if (id) {
        const newPriorities = priorities.filter(p => p?.id !== id);
        formik.setFieldValue("priority", newPriorities);
        setPriorities(newPriorities);
      }
    },
    [priorities]
  );

  const PrioridyComponent = useMemo(
    () =>
      type === ALT_MATERIAL_TYPE.MATERIAL
        ? props => <MaterialPriority {...props} />
        : props => <GradeGramPriority {...props} />,
    [type]
  );

  return (
    <>
      <Grid container className={classes.priorityDiv}>
        <Grid item className={classes.priorityLabel}>
          <Box>
            {" "}
            {priorities.map(
              p =>
                p.id &&
                !p.disabled && (
                  <Box
                    mb={2}
                    key={p.id}
                    style={{ minHeight: p.materialOsId ? 110 : 120 }}
                    className={classes.flex}
                  >
                    <MyTypography
                      variant="subtitle3"
                      className={classes.fonStyles}
                    >
                      <span style={{ display: "inline", whiteSpace: "nowrap" }}>
                        {`Priority ${priorities
                          .filter(e => !e.disabled)
                          .findIndex(e => e.id === p.id) + 1} `}
                      </span>
                    </MyTypography>
                  </Box>
                )
            )}
          </Box>
        </Grid>
        <Grid item xs={12} md={10} className={classes.priorityBox}>
          <DragHandle
            handleRearrangePriorty={handleRearrangePriorty}
            itemsToRender={priorities.map(p => ({
              id: p.id,
              element: !p.disabled && (
                <Box
                  mb={2}
                  key={p.id}
                  style={{
                    width: "100%",
                    minHeight: p.materialOsId ? 110 : 120
                  }}
                >
                  <Box className={classes.flex} alignItems="center">
                    <MyTypography variant={"subtitle3"}></MyTypography>
                  </Box>

                  <PrioridyComponent
                    isShowDeleteButton={
                      priorities.filter(e => !e.disabled).length > 1
                    }
                    value={p}
                    handleChange={handleChangePriority}
                    onRemove={handleRemovePriority}
                    id={p?.id}
                    key={p.id}
                    priority={p.id}
                    isSubmitted={isSubmitted}
                    saleOrganization={saleOrganization}
                  />
                </Box>
              )
            }))}
          />
        </Grid>
      </Grid>

      <Box
        className={classes.addingstyleprior}
        style={{
          justifyContent: priorities.length < 5 ? "space-between" : "flex-end"
        }}
      >
        <Box
          onClick={
            priorities.length < 5
              ? () => handleAddPriority(priorities.length)
              : undefined
          }
          mt={1}
          className={classes.addPriority}
          style={{
            display: priorities.length < 5 ? "flex" : "none"
          }}
          data-testid="add-priority-button"
        >
          <SVG stroke="#009FB8" height={"18px"} src={plusIcon} />{" "}
          <span
            style={{
              whiteSpace: "nowrap"
            }}
          >
            Add Priority
          </span>
        </Box>
        <Box
          className={classes.singleLineMessage}
          style={{
            display: priorities.length > 1 ? "flex" : "none"
          }}
        >
          <MyTypography
            variant="body3"
            align={"center"}
            className={classes.typo}
          >
            Press and hold{" "}
          </MyTypography>
          <SVG
            color="#999999"
            height={"20px"}
            style={{ margin: "0 3px" }}
            src={dragIcon}
          />
          <MyTypography
            variant="body3"
            align={"center"}
            className={classes.typo}
          >
            {" "}
            to drag and drop the Priority Card
          </MyTypography>
        </Box>
      </Box>
    </>
  );
};

const MaterialPriorityWrapper = memo(MaterialPriorityWrapperComponent);
const GradeGramPriority = memo(GradeGramPriorityComponent);
const MaterialPriority = memo(MaterialPriorityComponent);

export default MaterialPriorityWrapper;
